/**
 * Tab 05 · Tasks — the only tab that writes.
 *
 * Taken tasks live on the device (see `tasks.ts`) until reports exist
 * server-side; the screen says that plainly rather than showing points that
 * would vanish on another phone.
 *
 * Three tabs, one panel at a time: Today is what is still unlogged today and
 * opens by default, In progress is every taken task with today's work first, and
 * New is the library. The split exists because "in progress" answers a different
 * question from "what do I do right now", and mixing them buries the second.
 *
 * The ceiling is ~15 minutes a day, not a task count — five 30-minute exercises
 * is ninety minutes and nobody survives that to day four.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { useT, useTt, TFn } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent, AppContent, Exercise } from './content';
import { useProfile, Profile, PatState } from './profile';
import { Icon } from './icons';
import { XpTag } from './XpTag';
import { Appear, Chip, Col, Fluid, H1, Meta, Muted, Panel, Rail, Row, useIsDesktop } from './primitives';
import {
    c,
    hair,
    hair2,
    f,
    ACT,
    scoreColor,
    patternZone,
    effectiveStrength,
    strengthKey,
    DIR_ORDER,
    LOAD,
    DESKTOP,
    DAY_KEYS,
    xpBase,
} from './tokens';
import { R } from './routes';
import { TakenTask, useTasks, doneCount, streakOf, isDoneToday, weekOf, todayIndex, dayKey } from './tasks';

const DAY_MINUTES = 15;

type TimeFilter = 'all' | 2 | 5 | 15 | 30;

/** Translation keys — the chips render in the active language. */
const TIME_LABEL: Record<string, string> = {
    all: 'app.tasks.time.all',
    2: 'app.tasks.time.2',
    5: 'app.tasks.time.5',
    15: 'app.tasks.time.15',
    30: 'app.tasks.time.30',
};

const inTime = (min: number | null, t: TimeFilter): boolean => {
    if (t === 'all') return true;
    const m = min ?? 5;

    return t === 30 ? m > 15 : m <= t;
};

const WeekDots: React.FC<{ week: boolean[]; hex: string }> = ({ week, hex }) => {
    const t = useT();

    return (
        <Row style={{ gap: 6 }}>
            {week.map((on, i) => {
            // Today keeps a faint marker even when unlogged, so the row reads as
            // "here you are" rather than as one more empty day.
            let bg: string = c.track;

            if (on) bg = hex;
            else if (i === todayIndex()) bg = 'rgba(232,230,224,0.2)';

            return (
                <Col key={DAY_KEYS[i]} style={{ flex: 1, alignItems: 'center' }} gap={5}>
                    <div
                        style={{
                            width: '100%',
                            height: 5,
                            borderRadius: 3,
                            background: bg,
                            boxShadow: on ? `0 0 6px ${hex}66` : 'none',
                        }}
                    />
                    <span style={{ fontFamily: f.mono, fontSize: 9, color: on ? c.muted : c.subtle }}>
                        {t(DAY_KEYS[i])}
                    </span>
                </Col>
            );
        })}
        </Row>
    );
};

/* ── mobile card ───────────────────────────────────── */

const WorkCard: React.FC<{
    task: TakenTask;
    ex: Exercise;
    pattern?: PatState;
    content: AppContent;
    profile: Profile;
    onOpen: () => void;
    onNote: (v: string) => void;
    onDrop: () => void;
}> = ({ task, ex, pattern, content, profile, onOpen, onNote, onDrop }) => {
    const t = useT();
    const done = isDoneToday(task);
    const hex = pattern?.pct != null ? patternZone(pattern.pct, pattern.pol).hex : c.muted;
    const n = doneCount(task);
    const streak = streakOf(task);
    const [note, setNoteLocal] = React.useState(task.note);
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    return (
        <div
            style={{
                border: done ? hair : `1px solid ${hex}44`,
                borderRadius: 16,
                padding: '15px 16px',
                background: c.card,
                minWidth: 0,
            }}
        >
            <Row style={{ gap: 9 }}>
                <Meta style={{ fontSize: 9, letterSpacing: '0.12em', color: done ? '#4ade80' : hex }}>
                    {t(done ? 'app.tasks.doneToday' : 'app.tasks.daily')}
                </Meta>
                {streak > 0 && (
                    <Row style={{ gap: 5 }}>
                        <Icon k="flame" size={11} color="#f59e0b" />
                        <span style={{ fontFamily: f.mono, fontSize: 10.5, color: c.muted }}>{streak}</span>
                    </Row>
                )}
                <button
                    type="button"
                    aria-label={t('app.tasks.drop')}
                    onClick={onDrop}
                    style={{
                        marginLeft: 'auto',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        border: hair,
                        background: 'transparent',
                        display: 'grid',
                        placeItems: 'center',
                        cursor: 'pointer',
                    }}
                >
                    <Icon k="close" size={10} color={c.subtle} />
                </button>
            </Row>

            <Link to={R.task(ex.code)} style={{ textDecoration: 'none' }}>
                <Row style={{ gap: 10, marginTop: 10 }}>
                    <div
                        style={{
                            fontSize: 15.5,
                            color: c.textPri,
                            fontWeight: 400,
                            letterSpacing: '-0.015em',
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {ex.name}
                    </div>
                    <Icon k="chev" size={13} color={c.subtle} />
                </Row>
            </Link>
            <Meta style={{ fontSize: 9.5, letterSpacing: '0.08em', display: 'block', marginTop: 7 }}>
                {ex.min ? t('app.task.min', { n: ex.min }) : t('app.task.rule')} · {t(LOAD[ex.load] || ex.load)}
                {pattern ? ` · ${pattern.name}` : ''}
            </Meta>
            <XpTag ex={ex} content={content} scoreOf={scoreOf} style={{ marginTop: 10 }} />

            <div style={{ marginTop: 14 }}>
                <WeekDots week={weekOf(task)} hex={hex} />
            </div>
            <Row style={{ justifyContent: 'space-between', marginTop: 10 }}>
                <Meta style={{ fontSize: 9, color: c.subtle }}>
                    {t('app.tasks.ofDays', { n, days: task.days })}
                </Meta>
                <Meta style={{ fontSize: 9, color: c.subtle }}>{t('app.tasks.streak', { n: streak })}</Meta>
            </Row>

            {/* One note per task, not per day — it is what a weekly review reads. */}
            <div style={{ marginTop: 14 }}>
                <Meta
                    style={{ fontSize: 9, letterSpacing: '0.12em', color: c.subtle, display: 'block', marginBottom: 7 }}
                >
                    {t('app.tasks.note.label')}
                </Meta>
                <textarea
                    value={note}
                    onChange={(e) => setNoteLocal(e.target.value)}
                    onBlur={() => onNote(note)}
                    placeholder={t('app.tasks.note.placeholder')}
                    rows={2}
                    style={{
                        width: '100%',
                        resize: 'vertical',
                        background: c.cardIn,
                        border: note ? `1px solid ${ACT}55` : hair,
                        borderRadius: 12,
                        padding: '10px 12px',
                        color: c.text,
                        fontFamily: f.ui,
                        fontSize: 13,
                        lineHeight: 1.5,
                        outline: 'none',
                    }}
                />
            </div>

            <button
                type="button"
                onClick={onOpen}
                style={{
                    width: '100%',
                    height: 44,
                    marginTop: 12,
                    borderRadius: 99,
                    cursor: 'pointer',
                    border: done ? hair : 'none',
                    background: done ? 'transparent' : c.textPri,
                    color: done ? c.muted : '#0a0a0a',
                    fontFamily: f.ui,
                    fontSize: 14,
                    fontWeight: 500,
                }}
            >
                {t(done ? 'app.tasks.viewDay' : 'app.tasks.continue')}
            </button>
        </div>
    );
};

/* ── desktop row ───────────────────────────────────── */

const WorkRow: React.FC<{
    task: TakenTask;
    ex: Exercise;
    pattern?: PatState;
    content: AppContent;
    profile: Profile;
    first: boolean;
    onOpen: () => void;
    onNote: (v: string) => void;
    onDrop: () => void;
}> = ({ task, ex, pattern, content, profile, first, onOpen, onNote, onDrop }) => {
    const t = useT();
    const done = isDoneToday(task);
    const hex = pattern?.pct != null ? patternZone(pattern.pct, pattern.pol).hex : c.muted;
    const week = weekOf(task);
    const weekN = week.filter(Boolean).length;
    const streak = streakOf(task);
    const [note, setNoteLocal] = React.useState(task.note);
    const [noteOpen, setNoteOpen] = React.useState(false);
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    // Three button states — done, the day's first task, the rest.
    let btnBg = 'rgba(232,230,224,0.08)';
    let btnFg: string = c.textPri;

    if (done) {
        btnBg = 'transparent';
        btnFg = c.muted;
    } else if (first) {
        btnBg = c.textPri;
        btnFg = '#0a0a0a';
    }

    return (
        <div style={{ padding: '18px 0', borderTop: first ? 'none' : hair2, opacity: done ? 0.72 : 1 }}>
            <Row style={{ gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <Col style={{ flex: '1 1 260px', minWidth: 0 }} gap={7}>
                    <Row style={{ gap: 9, flexWrap: 'wrap' }}>
                        <Meta
                            style={{
                                fontSize: 9,
                                letterSpacing: '0.12em',
                                color: '#f59e0b',
                                border: '1px solid #f59e0b44',
                                borderRadius: 99,
                                padding: '3px 8px',
                            }}
                        >
                            {t('app.tasks.daily')}
                        </Meta>
                        {done && (
                            <Meta style={{ fontSize: 9, color: '#4ade80', letterSpacing: '0.12em' }}>
                                {t('app.tasks.done')}
                            </Meta>
                        )}
                        <Link to={R.task(ex.code)} style={{ textDecoration: 'none' }}>
                            <span
                                style={{
                                    fontSize: 15.5,
                                    color: c.textPri,
                                    fontWeight: 400,
                                    letterSpacing: '-0.015em',
                                    textDecoration: done ? 'line-through' : 'none',
                                    textDecorationColor: 'rgba(232,230,224,0.3)',
                                }}
                            >
                                {ex.name}
                            </span>
                        </Link>
                    </Row>
                    <Row style={{ gap: 8, flexWrap: 'wrap' }}>
                        <Icon k="tPats" size={11} color={hex} />
                        <Meta style={{ fontSize: 9.5 }}>{pattern?.name || content.dirBy[ex.track]?.disp}</Meta>
                        <Meta style={{ fontSize: 9.5, color: c.subtle }}>·</Meta>
                        <Meta style={{ fontSize: 9.5 }}>
                            {ex.min ? `${t('app.task.min', { n: ex.min })} · ` : ''}
                            {t(LOAD[ex.load] || ex.load)}
                        </Meta>
                        <button
                            type="button"
                            onClick={() => setNoteOpen((v) => !v)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        >
                            <Meta style={{ fontSize: 9.5, color: note ? c.text : c.subtle, letterSpacing: '0.1em' }}>
                                {t(note ? 'app.tasks.note.has' : 'app.tasks.note.add')}
                            </Meta>
                        </button>
                    </Row>
                </Col>

                {/* Controls stay one unit: they wrap under the title together
                    rather than each squeezing into its own narrow column. */}
                <Row style={{ gap: 16, flex: '1 1 420px', minWidth: 0, justifyContent: 'flex-end' }}>
                    <Col style={{ width: 132, flex: '0 0 auto' }} gap={8}>
                        <WeekDots week={week} hex={done ? '#4ade80' : hex} />
                        <Meta style={{ fontSize: 9, letterSpacing: '0.1em', textAlign: 'center' }}>
                            {t('app.tasks.ofThisWeek', { n: weekN, days: task.days })}
                        </Meta>
                    </Col>

                    {/* Fixed widths, not content widths: a "+10 CONNECTION" chip is
                        wider than "+10 BODY", and without this the week dots sit at
                        a different x on every row. */}
                    <XpTag
                        ex={ex}
                        content={content}
                        scoreOf={scoreOf}
                        style={{ width: 146, flex: '0 0 auto', alignItems: 'center' }}
                    />

                    <Row style={{ gap: 8, width: 146, flex: '0 0 auto', justifyContent: 'flex-end' }}>
                        <button
                            type="button"
                            onClick={onOpen}
                            style={{
                                minWidth: 104,
                                height: 38,
                                borderRadius: 99,
                                cursor: 'pointer',
                                border: done ? hair : 'none',
                                background: btnBg,
                                color: btnFg,
                                fontFamily: f.ui,
                                fontSize: 12.5,
                                fontWeight: 500,
                            }}
                        >
                            {t(done ? 'app.tasks.day' : 'app.tasks.continue')}
                        </button>
                        <button
                            type="button"
                            aria-label={t('app.tasks.drop')}
                            onClick={onDrop}
                            style={{
                                width: 30,
                                height: 30,
                                flex: '0 0 auto',
                                borderRadius: '50%',
                                border: hair,
                                background: 'transparent',
                                display: 'grid',
                                placeItems: 'center',
                                cursor: 'pointer',
                            }}
                        >
                            <Icon k="close" size={11} color={c.subtle} />
                        </button>
                    </Row>
                </Row>
            </Row>

            {noteOpen && (
                <div style={{ marginTop: 12 }}>
                    <textarea
                        value={note}
                        onChange={(e) => setNoteLocal(e.target.value)}
                        onBlur={() => onNote(note)}
                        placeholder={t('app.tasks.note.placeholderLong')}
                        rows={2}
                        style={{
                            width: '100%',
                            resize: 'vertical',
                            background: c.cardIn,
                            border: note ? `1px solid ${ACT}55` : hair,
                            borderRadius: 12,
                            padding: '10px 12px',
                            color: c.text,
                            fontFamily: f.ui,
                            fontSize: 13,
                            lineHeight: 1.5,
                            outline: 'none',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

/** Library card — desktop grid cell. */
const LibCard: React.FC<{
    ex: Exercise;
    content: AppContent;
    profile: Profile;
    taken: boolean;
    accent: boolean;
    onAdd: () => void;
}> = ({ ex, content, profile, taken, accent, onAdd }) => {
    const t = useT();
    const pat = ex.pats[0] ? content.patBy[ex.pats[0]] : null;
    const mine = pat ? profile.patterns.find((p) => p.code === pat.code && p.pct != null) : null;
    const st = profile.directions.find((x) => x.code === ex.track);
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    return (
        <Col
            style={{
                border: accent ? '1px solid rgba(232,230,224,0.16)' : hair,
                borderRadius: 14,
                padding: '13px 14px',
                background: c.cardIn,
                minWidth: 0,
            }}
            gap={9}
        >
            <Row style={{ gap: 10, alignItems: 'flex-start' }}>
                <Icon
                    k={ex.track}
                    size={14}
                    color={st?.deep ? scoreColor(st.score).hex : 'rgba(232,230,224,0.34)'}
                    style={{ marginTop: 2, flex: '0 0 auto' }}
                />
                <Link to={R.task(ex.code)} style={{ textDecoration: 'none', flex: 1, minWidth: 0 }}>
                    <span
                        style={{
                            fontSize: 13.5,
                            color: c.textPri,
                            fontWeight: 400,
                            letterSpacing: '-0.01em',
                            lineHeight: 1.3,
                        }}
                    >
                        {ex.name}
                    </span>
                </Link>
                <button
                    type="button"
                    aria-label={t(taken ? 'app.tasks.alreadyTaken' : 'app.tasks.open')}
                    onClick={taken ? undefined : onAdd}
                    disabled={taken}
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        display: 'grid',
                        placeItems: 'center',
                        border: taken ? 'none' : `1px solid ${c.textPri}`,
                        background: taken ? 'rgba(74,222,128,0.12)' : 'transparent',
                        cursor: taken ? 'default' : 'pointer',
                    }}
                >
                    <Icon k={taken ? 'check' : 'plus'} size={12} color={taken ? '#4ade80' : c.textPri} />
                </button>
            </Row>
            <Row style={{ gap: 7, flexWrap: 'wrap' }}>
                <Meta style={{ fontSize: 9 }}>
                    {ex.min ? `${t('app.task.min', { n: ex.min })} · ` : ''}
                    {t(LOAD[ex.load] || ex.load)}
                </Meta>
                {pat && (
                    <>
                        <Meta style={{ fontSize: 9, color: c.subtle }}>·</Meta>
                        <Meta style={{ fontSize: 9, color: mine ? c.text : c.subtle }}>
                            {pat.name}
                            {mine ? ` · ${t('app.tasks.yours')}` : ''}
                        </Meta>
                    </>
                )}
            </Row>
            <XpTag ex={ex} content={content} scoreOf={scoreOf} />
        </Col>
    );
};

/** Mobile library row — denser than the desktop card. */
const LibRow: React.FC<{
    ex: Exercise;
    content: AppContent;
    profile: Profile;
    taken: boolean;
    onAdd: () => void;
}> = ({ ex, content, profile, taken, onAdd }) => {
    const t = useT();
    const pat = ex.pats[0] ? content.patBy[ex.pats[0]] : null;
    const mine = pat ? profile.patterns.find((p) => p.code === pat.code && p.pct != null) : null;
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    return (
        <Row style={{ gap: 12, padding: '13px 0', borderTop: hair2, minWidth: 0 }}>
            <Icon k={ex.track} size={16} color={c.muted} />
            <Col style={{ flex: 1, minWidth: 0 }} gap={5}>
                <Row style={{ gap: 8 }}>
                    <Link to={R.task(ex.code)} style={{ textDecoration: 'none', minWidth: 0 }}>
                        <span
                            style={{
                                fontSize: 14,
                                color: c.textPri,
                                fontWeight: 400,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            {ex.name}
                        </span>
                    </Link>
                    {mine && (
                        <Chip color="#fb923c" border="#fb923c44" style={{ fontSize: 8.5, padding: '2px 7px' }}>
                            {t('app.tasks.yoursTag')}
                        </Chip>
                    )}
                </Row>
                <Meta style={{ fontSize: 9, letterSpacing: '0.08em' }}>
                    {ex.min ? t('app.task.min', { n: ex.min }) : t('app.task.rule')} ·{' '}
                    {t(LOAD[ex.load] || ex.load)}
                    {pat ? ` · ${pat.name}` : ''}
                </Meta>
                <XpTag ex={ex} content={content} scoreOf={scoreOf} style={{ marginTop: 2 }} />
            </Col>
            <button
                type="button"
                aria-label={t(taken ? 'app.tasks.alreadyTaken' : 'app.tasks.open')}
                onClick={taken ? undefined : onAdd}
                disabled={taken}
                style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    flex: '0 0 auto',
                    display: 'grid',
                    placeItems: 'center',
                    border: taken ? 'none' : hair,
                    background: taken ? 'rgba(74,222,128,0.12)' : 'transparent',
                    cursor: taken ? 'default' : 'pointer',
                }}
            >
                <Icon k={taken ? 'check' : 'plus'} size={14} color={taken ? '#4ade80' : c.textPri} />
            </button>
        </Row>
    );
};

/* ── screen ────────────────────────────────────────── */

const TasksPage: React.FC = () => {
    const { t, tn } = useTt();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);
    const navigate = useNavigate();
    const { list, drop, setNote } = useTasks();

    const [tab, setTab] = React.useState<'today' | 'work' | 'add'>('today');
    const [dirFilter, setDirFilter] = React.useState('all');
    const [timeFilter, setTimeFilter] = React.useState<TimeFilter>('all');

    const takenCodes = React.useMemo(() => new Set(list.map((t) => t.code)), [list]);

    /**
     * Open the task, do not take it. Taking it here skipped the start screen —
     * its question, its cycle and its Start button — and dropped the user straight
     * into an already-running task. The task screen owns the start.
     */
    const addAndOpen = React.useCallback((exCode: string) => navigate(R.task(exCode)), [navigate]);
    const patBy = React.useCallback(
        (code?: string) => (code ? profile.patterns.find((p) => p.code === code) : undefined),
        [profile.patterns],
    );

    const active = React.useMemo(() => {
        if (!content) return [];
        const pairs = list.map((t) => ({ t, ex: content.exercises.find((e) => e.code === t.code) }));

        // Still to do today first — a list that opens with what is already done
        // makes the user scroll to find the work.
        return pairs
            .filter((x): x is { t: TakenTask; ex: Exercise } => !!x.ex)
            .sort((a, b) => Number(isDoneToday(a.t)) - Number(isDoneToday(b.t)));
    }, [list, content]);

    /** What today still asks for — the default tab, and the only urgent one. */
    const todo = React.useMemo(() => active.filter((x) => !isDoneToday(x.t)), [active]);
    const shownTasks = tab === 'today' ? todo : active;
    const doneToday = active.filter((x) => isDoneToday(x.t)).length;
    const minutesToday = active.reduce((n, x) => n + (x.ex.min ?? 0), 0);

    /** Real week stats from the log — nothing modelled. */
    const week = React.useMemo(() => {
        const days: boolean[] = [];
        const d = new Date();

        d.setDate(d.getDate() - todayIndex());
        for (let i = 0; i < 7; i += 1) {
            const key = dayKey(d);

            days.push(active.some((x) => x.t.log?.[key]?.done));
            d.setDate(d.getDate() + 1);
        }
        // Compare day keys as strings. `new Date('2026-08-03')` is UTC midnight
        // while the keys are written in local time, so a timestamp comparison
        // moved the window by a day west of Greenwich. ISO days sort
        // lexicographically, so this needs no Date at all.
        const from = dayKey(new Date(Date.now() - 6 * 86400000));
        const marks = active.reduce(
            (n, x) => n + Object.entries(x.t.log || {}).filter(([k, e]) => e.done && k >= from).length,
            0,
        );
        const minutes = active.reduce((n, x) => {
            const hits = Object.entries(x.t.log || {}).filter(([k, e]) => e.done && k >= from).length;

            return n + hits * (x.ex.min ?? 0);
        }, 0);

        return { days, marks, minutes };
    }, [active]);

    const totalMarks = React.useMemo(() => active.reduce((n, x) => n + doneCount(x.t), 0), [active]);
    const bestStreak = React.useMemo(() => active.reduce((n, x) => Math.max(n, streakOf(x.t)), 0), [active]);

    const library = React.useMemo(() => {
        if (!content) return [];
        return content.exercises
            .filter((e) => (dirFilter === 'all' ? true : e.track === dirFilter))
            .filter((e) => inTime(e.min, timeFilter))
            .sort((a, b) => (a.min ?? 5) - (b.min ?? 5));
    }, [content, dirFilter, timeFilter]);

    /** Ranked by need, cheapest exercise first. Drives both the empty state and the rail. */
    const suggestions = React.useMemo(() => {
        if (!content) return [];
        const ranked = profile.patterns
            .filter((p) => p.pct != null)
            .sort((a, b) => effectiveStrength(b.pct as number, b.pol) - effectiveStrength(a.pct as number, a.pol));
        const out: Array<{ ex: Exercise; pattern: PatState }> = [];

        ranked.forEach((p) => {
            if (out.length >= 2) return;
            const pick = (content.exsByPattern[p.code] || [])
                .slice()
                .sort((a, b) => (a.min ?? 5) - (b.min ?? 5))
                .find((e) => !takenCodes.has(e.code));

            if (pick) out.push({ ex: pick, pattern: p });
        });
        return out;
    }, [content, profile.patterns, takenCodes]);

    const shellProps = {
        active: 'tasks' as const,
        title: t('app.tab.tasks'),
        subtitle: t('app.tasks.subtitle', {
            done: doneToday,
            total: active.length,
            min: minutesToday,
        }),
        index: profile.index,
        email: userProfile?.email || user?.email,
        tasksDone: doneToday,
        tasksTotal: active.length,
        chips: (
            <>
                {bestStreak > 0 && (
                    <Row style={{ gap: 6, padding: '7px 12px', borderRadius: 99, border: hair, background: c.card }}>
                        <Icon k="flame" size={13} color="#f59e0b" />
                        <span style={{ fontFamily: f.mono, fontSize: 12, color: c.textPri }}>{bestStreak}</span>
                    </Row>
                )}
                <Row style={{ gap: 16, padding: '6px 14px', borderRadius: 99, border: hair, background: c.card }}>
                    {(
                        [
                            [t('app.tasks.marks'), String(totalMarks)],
                            [t('app.tasks.points'), '–'],
                        ] as Array<[string, string]>
                    ).map(([l, v]) => (
                        <Row key={l} style={{ gap: 7 }}>
                            <Meta style={{ fontSize: 9 }}>{l}</Meta>
                            <span
                                style={{ fontFamily: f.mono, fontSize: 12.5, color: v === '–' ? c.subtle : c.textPri }}
                            >
                                {v}
                            </span>
                        </Row>
                    ))}
                </Row>
            </>
        ),
    };

    if (!content) {
        return (
            <AppShell {...shellProps}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }}>
                    <Meta style={{ color: c.subtle }}>{t('app.state.loading')}</Meta>
                </Col>
            </AppShell>
        );
    }

    const emptyWork = (
        <Col gap={12}>
            <Muted style={{ fontSize: 13 }}>{t('app.tasks.empty')}</Muted>
            {suggestions.map(({ ex, pattern }) => (
                <div
                    key={ex.code}
                    style={{ border: hair, borderRadius: 15, padding: '14px 15px', background: c.cardIn }}
                >
                    <Meta
                        style={{
                            fontSize: 9,
                            letterSpacing: '0.12em',
                            color: patternZone(pattern.pct as number, pattern.pol).hex,
                        }}
                    >
                        {pattern.name} · {t(strengthKey(pattern.pct as number, pattern.pol))}
                    </Meta>
                    <div style={{ fontSize: 14.5, color: c.textPri, fontWeight: 400, marginTop: 8 }}>{ex.name}</div>
                    <Meta style={{ fontSize: 9.5, display: 'block', marginTop: 6 }}>
                        {ex.min ? `${t('app.task.min', { n: ex.min })} · ` : ''}
                        {t(LOAD[ex.load] || ex.load)} · +{tn('app.state.points', xpBase(ex.min))}
                    </Meta>
                    <button
                        type="button"
                        onClick={() => addAndOpen(ex.code)}
                        style={{
                            width: '100%',
                            height: 40,
                            marginTop: 12,
                            borderRadius: 99,
                            border: 'none',
                            background: c.textPri,
                            color: '#0a0a0a',
                            fontFamily: f.ui,
                            fontSize: 13.5,
                            fontWeight: 500,
                            cursor: 'pointer',
                        }}
                    >
                        {t('app.tasks.take')}
                    </button>
                </div>
            ))}
            {!suggestions.length && (
                <Muted style={{ fontSize: 12.5 }}>{t('app.tasks.empty.noPicks')}</Muted>
            )}
        </Col>
    );

    const filters = (
        <Row style={{ gap: 5, flexWrap: 'wrap', paddingBottom: 14, borderBottom: hair2 }}>
            <button
                type="button"
                onClick={() => setDirFilter('all')}
                style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
            >
                <Chip
                    style={{
                        background: dirFilter === 'all' ? c.textPri : c.cardIn,
                        color: dirFilter === 'all' ? '#0a0a0a' : c.muted,
                        border: dirFilter === 'all' ? `1px solid ${c.textPri}` : hair,
                        padding: '6px 11px',
                        fontSize: 10,
                    }}
                >
                    {t('app.pats.filter.all', { n: content.exercises.length })}
                </Chip>
            </button>
            {DIR_ORDER.map((code) => {
                const on = dirFilter === code;
                const st = profile.directions.find((x) => x.code === code);
                const n = content.exercises.filter((e) => e.track === code).length;

                return (
                    <button
                        key={code}
                        type="button"
                        onClick={() => setDirFilter(on ? 'all' : code)}
                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                    >
                        <Row
                            style={{
                                gap: 5,
                                padding: '6px 9px',
                                borderRadius: 99,
                                background: on ? 'rgba(232,230,224,0.08)' : c.cardIn,
                                border: on ? '1px solid rgba(232,230,224,0.3)' : hair,
                            }}
                        >
                            <Icon
                                k={code}
                                size={11}
                                color={st?.deep ? scoreColor(st.score).hex : 'rgba(232,230,224,0.34)'}
                            />
                            <Meta style={{ fontSize: 9.5, letterSpacing: '0.1em', color: on ? c.textPri : c.muted }}>
                                {content.dirBy[code]?.disp}
                            </Meta>
                            <span style={{ fontFamily: f.mono, fontSize: 9, color: c.subtle }}>{n}</span>
                        </Row>
                    </button>
                );
            })}
            <div style={{ width: 1, background: 'rgba(232,230,224,0.1)', margin: '2px 4px' }} />
            {(['all', 2, 5, 15, 30] as TimeFilter[]).map((tf) => {
                const on = timeFilter === tf;

                return (
                    <button
                        key={String(tf)}
                        type="button"
                        onClick={() => setTimeFilter(on ? 'all' : tf)}
                        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}
                    >
                        <Chip
                            style={{
                                padding: '6px 10px',
                                fontSize: 9.5,
                                background: on ? 'rgba(232,230,224,0.08)' : c.cardIn,
                                border: on ? '1px solid rgba(232,230,224,0.3)' : hair,
                                color: on ? c.textPri : c.muted,
                            }}
                        >
                            {t(TIME_LABEL[String(tf)])}
                        </Chip>
                    </button>
                );
            })}
        </Row>
    );

    const localNote = (
        <Muted style={{ fontSize: 11.5 }}>{t('app.tasks.localNote')}</Muted>
    );

    const segment = (
        <Row style={{ gap: 4, background: c.card, border: hair, borderRadius: 99, padding: 4 }}>
            {(
                [
                    ['today', `${t('app.tasks.tab.today')} · ${todo.length}`],
                    ['work', `${t('app.tasks.tab.work')}${active.length ? ` · ${active.length}` : ''}`],
                    ['add', `${t('app.tasks.tab.new')} · ${content.exercises.length}`],
                ] as const
            ).map(([id, label]) => {
                const on = tab === id;

                return (
                    <button
                        key={id}
                        type="button"
                        onClick={() => setTab(id)}
                        style={{
                            flex: 1,
                            height: 34,
                            borderRadius: 99,
                            border: 'none',
                            cursor: 'pointer',
                            background: on ? c.textPri : 'transparent',
                            color: on ? '#0a0a0a' : c.muted,
                            fontFamily: f.ui,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {label}
                    </button>
                );
            })}
        </Row>
    );

    /* ── desktop ─────────────────────────────────────── */
    if (desktop) {
        return (
            <AppShell {...shellProps}>
                <Appear>
                    <DeskRow>
                        <Fluid>
                            <Muted style={{ fontSize: 13.5, maxWidth: 620 }}>
                                {t('app.tasks.intro', { min: DAY_MINUTES })}
                            </Muted>

                            {/* Same switcher as mobile, by request: the library
                                below the in-progress list pushed the rail out of
                                view on a laptop screen. One panel at a time keeps
                                both the task list and the week rail on screen. */}
                            <div style={{ maxWidth: 420 }}>{segment}</div>

                            {tab !== 'add' ? (
                                <Panel
                                    title={t(tab === 'today' ? 'app.day.today' : 'app.tasks.inProgress')}
                                    right={
                                        <Meta style={{ fontSize: 9.5 }}>
                                            {shownTasks.length
                                                ? t('app.tasks.countMin', {
                                                      n: shownTasks.length,
                                                      min: minutesToday,
                                                  })
                                                : t('app.tasks.none')}
                                        </Meta>
                                    }
                                    pad="6px 24px 18px"
                                >
                                    {shownTasks.length ? (
                                        <>
                                            {shownTasks.map(({ t, ex }, i) => (
                                                <WorkRow
                                                    key={t.code}
                                                    task={t}
                                                    ex={ex}
                                                    pattern={patBy(ex?.pats[0])}
                                                    content={content}
                                                    profile={profile}
                                                    first={i === 0}
                                                    onOpen={() => navigate(R.task(t.code))}
                                                    onNote={(v) => setNote(t.code, v)}
                                                    onDrop={() => drop(t.code)}
                                                />
                                            ))}
                                            {minutesToday > DAY_MINUTES && (
                                                <Muted style={{ fontSize: 12, marginTop: 14 }}>
                                                    {t('app.tasks.overCeiling', {
                                                        total: minutesToday,
                                                        cap: DAY_MINUTES,
                                                    })}
                                                </Muted>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ paddingTop: 14 }}>
                                            {tab === 'today' && active.length ? (
                                                <Muted style={{ fontSize: 13 }}>{t('app.tasks.allDone')}</Muted>
                                            ) : (
                                                emptyWork
                                            )}
                                        </div>
                                    )}
                                </Panel>
                            ) : (
                                <Panel
                                    title={t('app.tasks.newTasks')}
                                    right={
                                        <Meta style={{ fontSize: 9.5 }}>
                                            {t('app.tasks.libraryCount', {
                                                n: library.length,
                                                total: content.exercises.length,
                                            })}
                                        </Meta>
                                    }
                                    pad="16px 24px 20px"
                                >
                                    {filters}
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                            gap: 10,
                                            marginTop: 14,
                                        }}
                                    >
                                        {/* The whole library, not a first page: a
                                            filtered list that silently stops at 12
                                            reads as "this is everything there is". */}
                                        {library.map((ex, i) => (
                                            <LibCard
                                                key={ex.code}
                                                ex={ex}
                                                content={content}
                                                profile={profile}
                                                taken={takenCodes.has(ex.code)}
                                                accent={i === 0}
                                                onAdd={() => addAndOpen(ex.code)}
                                            />
                                        ))}
                                    </div>
                                    {!library.length && (
                                        <Muted style={{ fontSize: 12.5, marginTop: 14 }}>
                                            {t('app.tasks.library.empty')}
                                        </Muted>
                                    )}
                                </Panel>
                            )}
                        </Fluid>

                        <Rail width={300}>
                            <Panel
                                title={t('app.ins.week')}
                                right={
                                    bestStreak > 0 ? (
                                        <Row style={{ gap: 6 }}>
                                            <Icon k="flame" size={12} color="#f59e0b" />
                                            <Meta style={{ fontSize: 9.5, color: '#f59e0b' }}>
                                                {tn('app.tasks.streakDays', bestStreak)}
                                            </Meta>
                                        </Row>
                                    ) : undefined
                                }
                                pad="16px 18px 18px"
                            >
                                <Row style={{ gap: 6 }}>
                                    {week.days.map((on, i) => {
                                        const today = i === todayIndex();
                                        let box = hair;

                                        if (on) box = '1px solid rgba(74,222,128,0.3)';
                                        else if (today) box = `1px dashed ${ACT}55`;

                                        return (
                                            <Col key={DAY_KEYS[i]} style={{ flex: 1, alignItems: 'center' }} gap={6}>
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: 28,
                                                        borderRadius: 8,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        background: on ? 'rgba(74,222,128,0.12)' : 'transparent',
                                                        border: box,
                                                    }}
                                                >
                                                    {on ? (
                                                        <Icon k="check" size={11} color="#4ade80" />
                                                    ) : (
                                                        <span
                                                            style={{
                                                                fontFamily: f.mono,
                                                                fontSize: 9,
                                                                color: today ? ACT : c.subtle,
                                                            }}
                                                        >
                                                            {today ? '•' : '–'}
                                                        </span>
                                                    )}
                                                </div>
                                                <span
                                                    style={{
                                                        fontFamily: f.mono,
                                                        fontSize: 9,
                                                        color: today ? c.textPri : c.subtle,
                                                    }}
                                                >
                                                    {t(DAY_KEYS[i])}
                                                </span>
                                            </Col>
                                        );
                                    })}
                                </Row>
                                <Row style={{ gap: 16, marginTop: 18, paddingTop: 15, borderTop: hair2 }}>
                                    {(
                                        [
                                            [t('app.tasks.marks'), String(week.marks)],
                                            [t('app.tasks.minutes'), String(week.minutes)],
                                            [t('app.tasks.points'), '–'],
                                        ] as Array<[string, string]>
                                    ).map(([l, v]) => (
                                        <Col key={l} style={{ flex: 1, minWidth: 0 }} gap={4}>
                                            <span
                                                style={{
                                                    fontFamily: f.mono,
                                                    fontSize: 18,
                                                    fontWeight: 300,
                                                    letterSpacing: '-0.03em',
                                                    color: v === '–' ? c.subtle : c.textPri,
                                                }}
                                            >
                                                {v}
                                            </span>
                                            <Meta style={{ fontSize: 8.5, letterSpacing: '0.12em' }}>{l}</Meta>
                                        </Col>
                                    ))}
                                </Row>
                            </Panel>

                            {suggestions.length > 0 && (
                                <Panel title={t('app.tasks.recommended')} pad="14px 16px 16px">
                                    <Col gap={9}>
                                        {suggestions.map(({ ex, pattern }, i) => {
                                            const pz = patternZone(pattern.pct as number, pattern.pol);

                                            return (
                                                <div
                                                    key={ex.code}
                                                    style={{
                                                        border: i === 0 ? `1px solid ${pz.hex}3d` : hair,
                                                        borderRadius: 14,
                                                        padding: '13px 14px',
                                                        background: c.cardIn,
                                                    }}
                                                >
                                                    <Row style={{ gap: 8, marginBottom: 9 }}>
                                                        <span
                                                            style={{
                                                                width: 5,
                                                                height: 5,
                                                                borderRadius: '50%',
                                                                background: pz.hex,
                                                                flex: '0 0 auto',
                                                            }}
                                                        />
                                                        <Meta
                                                            style={{ fontSize: 9, color: pz.hex, flex: 1, minWidth: 0 }}
                                                        >
                                                            {pattern.name}
                                                        </Meta>
                                                        <span
                                                            style={{
                                                                fontFamily: f.mono,
                                                                fontSize: 10.5,
                                                                color: pz.hex,
                                                            }}
                                                        >
                                                            {Math.round(pattern.pct as number)}
                                                        </span>
                                                    </Row>
                                                    <span style={{ fontSize: 13.5, color: c.textPri, fontWeight: 400 }}>
                                                        {ex.name}
                                                    </span>
                                                    <Meta style={{ fontSize: 9, display: 'block', marginTop: 8 }}>
                                                        {ex.min ? `${t('app.task.min', { n: ex.min })} · ` : ''}
                                                        {t(LOAD[ex.load] || ex.load)}
                                                    </Meta>
                                                    <button
                                                        type="button"
                                                        onClick={() => addAndOpen(ex.code)}
                                                        style={{
                                                            width: '100%',
                                                            height: 32,
                                                            marginTop: 11,
                                                            borderRadius: 99,
                                                            border: 'none',
                                                            background: i === 0 ? c.textPri : 'rgba(232,230,224,0.08)',
                                                            color: i === 0 ? '#0a0a0a' : c.textPri,
                                                            fontFamily: f.ui,
                                                            fontSize: 12,
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {t('app.tasks.takeShort')}
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </Col>
                                </Panel>
                            )}

                            <Panel title={t('app.tasks.howCounted')} pad="16px 18px 18px">
                                {localNote}
                            </Panel>
                        </Rail>
                    </DeskRow>
                </Appear>
            </AppShell>
        );
    }

    /** An empty Today means done, not empty — that is a different sentence. */
    const emptyDay =
        tab === 'today' && active.length ? (
            <Muted style={{ fontSize: 13 }}>{t('app.tasks.allDone')}</Muted>
        ) : (
            emptyWork
        );

    /* ── mobile ──────────────────────────────────────── */
    const mobileCards = shownTasks.map(({ t, ex }) => (
        <WorkCard
            key={t.code}
            task={t}
            ex={ex}
            pattern={patBy(ex?.pats[0])}
            content={content}
            profile={profile}
            onOpen={() => navigate(R.task(t.code))}
            onNote={(v) => setNote(t.code, v)}
            onDrop={() => drop(t.code)}
        />
    ));
    const mobileWork = (
        <Col gap={11}>
            {shownTasks.length ? mobileCards : emptyDay}
            {minutesToday > DAY_MINUTES && (
                <Muted style={{ fontSize: 12 }}>
                    {t('app.tasks.overCeilingShort', { total: minutesToday, cap: DAY_MINUTES })}
                </Muted>
            )}
        </Col>
    );

    return (
        <AppShell {...shellProps}>
            <Appear>
                <Col style={{ padding: '10px 20px 0' }} gap={11}>
                    <H1>{t('app.tab.tasks')}</H1>
                    <Muted>{t('app.tasks.introShort')}</Muted>
                </Col>
                <div style={{ padding: '16px 20px 0' }}>{segment}</div>
                <div style={{ padding: '18px 20px 0' }}>
                    {tab !== 'add' ? (
                        mobileWork
                    ) : (
                        <>
                            {filters}
                            <div style={{ marginTop: 4 }}>
                                {library.length ? (
                                    library.map((ex) => (
                                        <LibRow
                                            key={ex.code}
                                            ex={ex}
                                            content={content}
                                            profile={profile}
                                            taken={takenCodes.has(ex.code)}
                                            onAdd={() => addAndOpen(ex.code)}
                                        />
                                    ))
                                ) : (
                                    <Muted style={{ fontSize: 12.5, paddingTop: 14 }}>
                                        {t('app.tasks.library.empty')}
                                    </Muted>
                                )}
                            </div>
                        </>
                    )}
                </div>
                <div style={{ padding: '20px 20px 26px' }}>{localNote}</div>
            </Appear>
        </AppShell>
    );
};

export default TasksPage;
